import type { EnvironmentOption } from './EnvironmentTabsRow/EnvironmentTabsRow.static'

export const DEFAULT_ENVIRONMENT = 'dev1'
export const PRODUCTION_ENVIRONMENT = 'prod'

export type EnvironmentTab = 'prod' | 'int'

export interface EnvironmentTabOption {
  readonly tab: EnvironmentTab
  readonly title: string
}

export const ENVIRONMENT_TABS: readonly EnvironmentTabOption[] = [
  { tab: 'prod', title: 'PROD' },
  { tab: 'int', title: 'INT' },
]

// Shared across EnvironmentTabsRow and AddGovernorDialog (both let the operator pick a dev instance).
export const BASE_INT_ENVIRONMENT = 'int'

export const ENVIRONMENT_OPTIONS: readonly EnvironmentOption[] = [
  // The bare "int" instance has no dev-number prefix, unlike DEV0-DEV24 -- label it accordingly
  // instead of "INT", which would be confusing duplicated under the INT tab itself.
  { label: 'PREFIXLESS', value: BASE_INT_ENVIRONMENT },
  ...Array.from({ length: 25 }, (_, index) => ({
    label: `DEV${index}`,
    value: `dev${index}`,
  })),
]

// environmentLabel is shared between EnvironmentTabsRow's dev-instance select and
// AddGovernorDialog's own environment field. addGovernorButton labels both the
// summary row's button and the add dialog's title/submit action.
// cancelButton and noRegistryChangeTitle are shared by both governor dialogs.
export const localText = {
  environmentLabel: 'Environment',
  addGovernorButton: 'Add Governor',
  cancelButton: 'Cancel',
  defaultErrorMessage: 'The registry request could not be completed.',
  governorAddedTitle: 'Governor added',
  governorRemovedTitle: 'Governor removed',
  noRegistryChangeTitle: 'No registry change',
} as const

// Registry values are the raw Core API business object keys; shown across RegistryTable,
// RegistryFilters, AddGovernorDialog, and RemoveGovernorDialog. Sourced from cdh-core-internal's
// cdh-core-config.yaml (business_object.instances) -- the same data GET /{hub}/businessObjects
// returns as `friendlyName`, just not something our own backend/frontend fetches live yet.
const BUSINESS_OBJECT_FRIENDLY_NAMES: Readonly<Record<string, string>> = {
  aftersales: 'Customer Support Data',
  alphabet: 'Alphabet',
  cm: 'Corporate Management Data',
  customer: 'Customer Data',
  dto: 'CDH Internal',
  facility: 'Places Data',
  finance: 'Finance Data',
  finserv: 'Financial Services Data',
  hr: 'People Data',
  itdata: 'IT Data',
  ito: 'ItO Data',
  marketres: 'Market Research & Structure Data',
  motorbike: 'Motorbike Data',
  otd: 'OtD Data',
  pursup: 'Purchasing & Supplier Data',
  quality: 'Quality Data',
  rollsroyce: 'Rolls-Royce Data',
  sales: 'Sales Data',
  salesperf: 'Sales Performance Data',
  vehicle: 'In-Vehicle Data',
}

export function businessObjectFriendlyName(businessObject: string): string {
  return BUSINESS_OBJECT_FRIENDLY_NAMES[businessObject] ?? businessObject
}

// Used where the operator picks a business object to submit (AddGovernorDialog) so they can see
// and double check the raw key that's actually sent, alongside the friendly name.
export function businessObjectFriendlyNameWithKey(businessObject: string): string {
  const friendlyName = businessObjectFriendlyName(businessObject)
  return friendlyName === businessObject ? businessObject : `${friendlyName} (${businessObject})`
}

// Unlike business objects, hub friendly names aren't in cdh-core-config.yaml -- they only exist
// in the live GET /config response our backend's GET /hubs passes through, so there's no static
// fallback map here; an unknown/not-yet-loaded hub just falls back to its uppercased raw key.
export function hubFriendlyName(hub: string, hubFriendlyNames: Readonly<Record<string, string>>): string {
  return hubFriendlyNames[hub] ?? hub.toUpperCase()
}
